import { useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { ExtendedAppointment, ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { Text } from '@frontend/design-system';
import { convertScheduleRequestEventToRequest } from '../../../hooks/scheduler-v3/use-convert-schedule-request-to-event';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../../stores/schedule-appointment-info-panel.store';
import { ScheduleRequestList } from '../../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestList/ScheduleRequestList';
import { AppointmentList } from './appointment-list';
import { PanelHeader } from './panel-header';

const getAppointmentsRangeText = (selectedAppointments: ExtendedAppointment[]) => {
  if (!selectedAppointments.length) return '';

  const appointmentTimes = selectedAppointments
    .filter((appointment) => appointment.start && appointment.duration)
    .map((appointment) => {
      const startTime = dayjs(appointment.start);
      const endTime = startTime.add(appointment.duration || 0, 'minute');
      return { start: startTime, end: endTime };
    });

  if (appointmentTimes.length === 0) return '';

  const minStartTime = appointmentTimes.reduce((min, current) =>
    current.start.isBefore(min.start) ? current : min
  ).start;

  const maxEndTime = appointmentTimes.reduce((max, current) => (current.end.isAfter(max.end) ? current : max)).end;

  return `${minStartTime.format('h:mm A')} - ${maxEndTime.format('h:mm A')}`;
};

export const OverlappingAppointmentsPanel = () => {
  const { t } = useTranslation('schedule');
  const { selectedAppointments: appointments, selectedRequests: scheduleRequestEvents } =
    useScheduleAppointmentInfoPanelShallowStore('selectedAppointments', 'selectedRequests');

  const scheduleRequests = useMemo(
    () =>
      uniqBy(scheduleRequestEvents, 'requestId')
        .map((req) => convertScheduleRequestEventToRequest(req.request))
        .filter((req): req is ScheduleRequest => !!req),
    [scheduleRequestEvents]
  );
  const providerName =
    appointments[0]?.practitionerName ?? scheduleRequests[0]?.requestedOpenings?.[0].assets?.providers?.[0] ?? '';

  const appointmentTimeRange = useMemo(() => getAppointmentsRangeText(appointments), [appointments]);
  const requestsTimeRange = useMemo(() => getScheduleRequestsRangeText(scheduleRequestEvents), [scheduleRequestEvents]);
  const appointmentInfoText = t('{{count}} appointments • {{timeRange}}', {
    count: appointments.length,
    timeRange: appointmentTimeRange,
  });
  const scheduleRequestInfoText = t('{{count}} appointment requests • {{timeRange}}', {
    count: scheduleRequests.length,
    timeRange: requestsTimeRange,
  });
  const hasAppointments = appointments.length > 0;
  const hasScheduleRequests = scheduleRequests.length > 0;

  return (
    <section css={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <PanelHeader
        appointmentsInfoText={hasAppointments || !hasScheduleRequests ? appointmentInfoText : undefined}
        providerName={providerName}
        requestsInfoText={hasScheduleRequests ? scheduleRequestInfoText : undefined}
      />
      {hasAppointments && (
        <>
          {hasScheduleRequests && (
            <Text size='medium' weight='bold' css={{ padding: 'var(--fabric-space-gap-sm)' }}>
              {t('Appointments')}
            </Text>
          )}
          <AppointmentList appointments={appointments} />
        </>
      )}
      {hasScheduleRequests && (
        <>
          {hasAppointments && (
            <Text size='medium' weight='bold' css={{ padding: 'var(--fabric-space-gap-sm)' }}>
              {t('Appointment Requests')}
            </Text>
          )}
          <ScheduleRequestList scheduleRequests={scheduleRequests} css={styles.scheduleRequestList} />
        </>
      )}
    </section>
  );
};

const styles = {
  scheduleRequestList: css`
    flex: 1;
  `,
};

const getScheduleRequestsRangeText = (scheduleRequests: ScheduleRequestEvent[]) => {
  const requestTimes = scheduleRequests
    .map((request) => {
      if (!request || !request.startTime || !request.endTime) return null;
      const startTime = dayjs(`${request.startDate} ${request.startTime}`).toDate();
      const endTime = dayjs(`${request.endDate} ${request.endTime}`).toDate();
      if (!startTime || !endTime) return null;
      return { start: startTime, end: endTime };
    })
    .filter((time): time is { start: Date; end: Date } => time !== null);

  if (requestTimes.length === 0) return '';

  const minStartTime = requestTimes.reduce((min, current) => (current.start < min.start ? current : min)).start;

  const maxEndTime = requestTimes.reduce((max, current) => (current.end > max.end ? current : max)).end;

  return `${minStartTime.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })} - ${maxEndTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};
